import React, { useState, useEffect } from "react";
import { EditButton, ShowButton } from "@refinedev/antd";
import { useList, useCustomMutation, useNotification } from "@refinedev/core";
import { Button, Space, Table, Tag, Tooltip, Modal, Form, Select, Input, message, Card } from "antd";
import { PrinterOutlined, SyncOutlined, PlusOutlined, FileTextOutlined, SearchOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { PageHeader } from "../../components/ui";
import { invoiceRegenerationService, RegenerationRequest } from "../../api/invoiceRegenerationService";

const { TextArea } = Input;

interface Quotation {
    id: number;
    quotationNumber: string;
    customerName: string;
    quotationDate: string;
    validUntil: string;
    status: string;
    totalAmount: number;
    documentUrl?: string;
}

export const QuotationList: React.FC = () => {
    const [searchText, setSearchText] = useState<string>("");
    const [filteredItems, setFilteredItems] = useState<Quotation[]>([]);
    const [regenerateModalVisible, setRegenerateModalVisible] = useState(false);
    const [selectedQuotation, setSelectedQuotation] = useState<Quotation | null>(null);
    const [regenerating, setRegenerating] = useState(false);
    const [form] = Form.useForm();
    const navigate = useNavigate();
    const { open } = useNotification();

    const { data, isLoading, refetch } = useList({
        resource: "quotations",
    });

    const { mutate: send } = useCustomMutation();
    const { mutate: accept } = useCustomMutation();
    const { mutate: reject } = useCustomMutation();
    const { mutate: convert } = useCustomMutation();
    const { mutate: generatePdf } = useCustomMutation();

    useEffect(() => {
        if (!data?.data) return;

        let quotations = data.data as Quotation[];

        if (searchText.trim() !== "") {
            const searchLower = searchText.toLowerCase();
            quotations = quotations.filter((q: Quotation) =>
                q.quotationNumber?.toLowerCase().includes(searchLower) ||
                q.customerName?.toLowerCase().includes(searchLower) ||
                q.status?.toLowerCase().includes(searchLower)
            );
        }

        const sorted = [...quotations].sort((a, b) =>
            new Date(b.quotationDate).getTime() - new Date(a.quotationDate).getTime()
        );

        setFilteredItems(sorted);
    }, [data, searchText]);

    const handleSend = (id: number) => {
        send({ url: `quotations/${id}/send`, method: "post" }, { onSuccess: () => refetch() });
    };

    const handleAccept = (id: number) => {
        accept({ url: `quotations/${id}/accept`, method: "post" }, { onSuccess: () => refetch() });
    };

    const handleReject = (id: number) => {
        reject({ url: `quotations/${id}/reject`, method: "post" }, { onSuccess: () => refetch() });
    };

    const handleConvert = (id: number) => {
        convert({ url: `quotations/${id}/convert`, method: "post" }, { onSuccess: () => refetch() });
    };

    const handlePrint = (record: Quotation) => {
        generatePdf({ url: `quotations/${record.id}/pdf`, method: "get" }, {
            onSuccess: (data: any) => {
                const documentUrl = data?.data?.documentUrl || data?.documentUrl;
                if (documentUrl) {
                    refetch();
                    window.open(documentUrl, '_blank');
                }
            },
        });
    };

    const handleRegenerateSubmit = async (values: any) => {
        if (!selectedQuotation) return;
        setRegenerating(true);
        try {
            const request: RegenerationRequest = {
                regenerationType: values.regenerationType,
                reason: values.reason,
                preserveOriginal: true,
                notifyCustomer: false,
            };
            const result = await invoiceRegenerationService.regenerateQuotation(selectedQuotation.id, request);
            if (result.success) {
                message.success(`Quotation ${selectedQuotation.quotationNumber} regenerated!`);
                setRegenerateModalVisible(false);
                refetch();
            } else {
                message.error(`Failed: ${result.errorMessage}`);
            }
        } catch (error: any) {
            message.error(`Failed: ${error.message}`);
        } finally {
            setRegenerating(false);
        }
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case "Sent": return "processing";
            case "Draft": return "default";
            case "Accepted": return "success";
            case "Rejected": return "error";
            case "Expired": return "warning";
            case "Converted": return "purple";
            default: return "default";
        }
    };

    const columns = [
        {
            title: "Quotation #",
            dataIndex: "quotationNumber",
            key: "quotationNumber",
            render: (value: string) => (
                <span className="font-medium text-text-primary">{value || "-"}</span>
            ),
        },
        {
            title: "Customer",
            dataIndex: "customerName",
            key: "customerName",
            render: (value: string) => (
                <span className="text-text-secondary">{value || "-"}</span>
            ),
        },
        {
            title: "Date",
            dataIndex: "quotationDate",
            key: "quotationDate",
            render: (value: string) => (
                <span className="text-text-secondary text-sm">
                    {value ? new Date(value).toLocaleDateString() : "-"}
                </span>
            ),
            responsive: ['md'] as any,
        },
        {
            title: "Valid Until",
            dataIndex: "validUntil",
            key: "validUntil",
            render: (value: string) => (
                <span className="text-text-secondary text-sm">
                    {value ? new Date(value).toLocaleDateString() : "-"}
                </span>
            ),
            responsive: ['lg'] as any,
        },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            render: (value: string) => (
                <Tag color={getStatusColor(value)} className="status-tag">{value}</Tag>
            ),
        },
        {
            title: "Amount",
            dataIndex: "totalAmount",
            key: "totalAmount",
            render: (value: number) => (
                <span className="font-semibold text-primary">${value?.toFixed(2) || '0.00'}</span>
            ),
        },
        {
            title: "Actions",
            key: "actions",
            width: 200,
            render: (_: any, record: Quotation) => (
                <Space size="small" wrap>
                    <EditButton hideText size="small" recordItemId={record.id} disabled={record.status !== "Draft"} />
                    <ShowButton hideText size="small" recordItemId={record.id} />
                    <Tooltip title="Print">
                        <Button size="small" icon={<PrinterOutlined />} onClick={() => handlePrint(record)} />
                    </Tooltip>
                    {record?.documentUrl && (
                        <Tooltip title="Regenerate">
                            <Button size="small" icon={<SyncOutlined />} onClick={() => { setSelectedQuotation(record); setRegenerateModalVisible(true); }} />
                        </Tooltip>
                    )}
                    {record.status === "Draft" && (
                        <Button size="small" type="primary" onClick={() => handleSend(record.id)}>Send</Button>
                    )}
                    {record.status === "Sent" && (
                        <>
                            <Button size="small" type="primary" onClick={() => handleAccept(record.id)}>Accept</Button>
                            <Button size="small" danger onClick={() => handleReject(record.id)}>Reject</Button>
                        </>
                    )}
                    {record.status === "Accepted" && (
                        <Button size="small" type="primary" onClick={() => handleConvert(record.id)}>Convert</Button>
                    )}
                </Space>
            ),
        },
    ];

    return (
        <div className="page-container">
            <PageHeader
                title="Quotations"
                titleIcon={<FileTextOutlined />}
                breadcrumbs={[{ title: "Quotations" }]}
                actions={[
                    {
                        label: "Create",
                        onClick: () => navigate("/quotations/create"),
                        type: "primary",
                        icon: <PlusOutlined />,
                    },
                ]}
            />

            <div className="data-table-card">
                <Card>
                    <div style={{ marginBottom: 16 }}>
                        <Input
                            placeholder="Search by quotation number, customer, or status"
                            prefix={<SearchOutlined className="text-text-muted" />}
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            style={{ maxWidth: 400 }}
                            allowClear
                            size="large"
                        />
                    </div>

                    <Table
                        dataSource={filteredItems}
                        columns={columns}
                        rowKey="id"
                        loading={isLoading}
                        pagination={{
                            pageSize: 10,
                            showSizeChanger: true,
                            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
                        }}
                        className="custom-table"
                        scroll={{ x: 1000 }}
                    />
                </Card>
            </div>

            <Modal
                title={`Regenerate Quotation ${selectedQuotation?.quotationNumber || ""}`}
                open={regenerateModalVisible}
                onCancel={() => { setRegenerateModalVisible(false); setSelectedQuotation(null); }}
                footer={null}
                width={500}
            >
                <Form form={form} layout="vertical" onFinish={handleRegenerateSubmit} initialValues={{ regenerationType: "BugFix" }}>
                    <Form.Item name="regenerationType" label="Regeneration Type" rules={[{ required: true }]}>
                        <Select>
                            <Select.Option value="BugFix">Bug Fix</Select.Option>
                            <Select.Option value="LayoutUpdate">Layout Update</Select.Option>
                            <Select.Option value="Manual">Manual</Select.Option>
                        </Select>
                    </Form.Item>
                    <Form.Item name="reason" label="Reason (Optional)">
                        <TextArea rows={3} placeholder="Brief explanation" />
                    </Form.Item>
                    <Form.Item>
                        <Space>
                            <Button type="primary" htmlType="submit" loading={regenerating}>Regenerate</Button>
                            <Button onClick={() => setRegenerateModalVisible(false)}>Cancel</Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};
